import {useCallback, useRef, useState} from 'react'
import type {SyntheticChangeEmitter} from '@github-ui/use-synthetic-change'

import {useGeneratePullRequestSummaryMutation} from '../../../mutations/use-generate-pull-request-summary-mutation'

// `@github-ui/copilot-markdown-toolbar-button` is an application-entrypoint package and can't be
// depended on here, so the default prompt it uses for its staff-only prompt editor is duplicated
// below. Keep in sync with that package's `prompts.ts` (source: `overall_summary_template.text.erb`).
const DEFAULT_SUMMARY_PROMPT = `Assistant is a senior software engineer explaining a pull request to a junior software engineer.

## Instructions:
Given a list of changes, create a concise and comprehensive summary of the most important changes in an easy-to-understand format. The length of the summary should be appropriate for the length and complexity of the changes: if there are only a few small changes, answer with a short summary without restating the changes. If there are multiple changes, begin your answer with a summary paragraph followed by a markdown list of the most important changes, ordered by importance. Include at a maximum only the top five most important changes in this list. Divide the changes in the list into groups by theme. Include the appropriate encoded references provided in the problem statement at the end of each change description in the list. The encoded references should be placed inside parentheses at the end of the line. Enclose code identifiers and file names in single backticks.`

/** Shown in the editor body at the insertion point while a summary is generating. */
export const GENERATING_PLACEHOLDER = '[Copilot is generating a summary...]'

/** A leading newline so the inserted text starts on its own line, unless already at the start of the value or right after an existing newline. */
function newlinePrefixFor(value: string, point: number): string {
  const before = value.slice(0, point)
  return before === '' || before.endsWith('\n') ? '' : '\n'
}

export interface UsePullRequestSummaryOptions {
  baseRevision: string
  headRevision: string
  headRepositoryId: number
  completionsUrl: string
  /** Reads the editor's current textarea/input element, used to find the insertion point and to locate previously-inserted text. */
  getEditorElement: () => HTMLTextAreaElement | HTMLInputElement | null
  /**
   * Emits a change into the editor exactly as though the user had typed it. This keeps the
   * caret, any unrelated selection, and undo history intact -- unlike driving the editor's value
   * through `setState`, which resets all of that.
   */
  emitChange: SyntheticChangeEmitter
}

/**
 * Drives the "Generate summary" Copilot action for the pull request description editor:
 * menu/prompt-editor open state, the generation request + job poll (via
 * `useGeneratePullRequestSummaryMutation`), and inserting the result into the editor.
 */
export function usePullRequestSummary({
  baseRevision,
  headRevision,
  headRepositoryId,
  completionsUrl,
  getEditorElement,
  emitChange,
}: UsePullRequestSummaryOptions) {
  const mutation = useGeneratePullRequestSummaryMutation(completionsUrl)
  const isGenerating = mutation.isPending
  const errorMessage = mutation.error ? mutation.error.message || 'Copilot encountered a temporary error.' : undefined
  const [isStale, setIsStale] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [promptEditorOpen, setPromptEditorOpen] = useState(false)
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_SUMMARY_PROMPT)
  const previousJobIdRef = useRef<string | null>(null)
  const insertedRef = useRef<string | null>(null)
  const insertedWithPrefixRef = useRef<string | null>(null)

  /**
   * Replaces the previously-inserted placeholder with `newText`, keeping its leading newline. A
   * true no-op (doesn't touch the editor at all) if nothing is currently inserted -- e.g. the
   * generation already settled, or `discardGeneration` ran on a Cancel with nothing in flight.
   */
  const replaceInsertedText = useCallback(
    (newText: string) => {
      const inserted = insertedRef.current
      insertedRef.current = null
      insertedWithPrefixRef.current = null
      if (inserted === null) return

      const index = getEditorElement()?.value.indexOf(inserted) ?? -1
      if (index === -1) return

      emitChange(newText, [index, index + inserted.length])
    },
    [getEditorElement, emitChange],
  )

  /** Removes the previously-inserted placeholder entirely, including its leading newline. Same no-op guard as `replaceInsertedText`. */
  const removeInsertedText = useCallback(() => {
    const insertedWithPrefix = insertedWithPrefixRef.current
    insertedRef.current = null
    insertedWithPrefixRef.current = null
    if (insertedWithPrefix === null) return

    const index = getEditorElement()?.value.indexOf(insertedWithPrefix) ?? -1
    if (index === -1) return

    emitChange('', [index, index + insertedWithPrefix.length])
  }, [getEditorElement, emitChange])

  const generateSummary = useCallback(
    (overridePrompt?: string) => {
      setMenuOpen(false)
      setPromptEditorOpen(false)
      setIsStale(false)

      const element = getEditorElement()
      const value = element?.value ?? ''
      const point = element?.selectionEnd ?? value.length
      const prefix = newlinePrefixFor(value, point)

      insertedRef.current = GENERATING_PLACEHOLDER
      insertedWithPrefixRef.current = prefix + GENERATING_PLACEHOLDER
      emitChange(prefix + GENERATING_PLACEHOLDER)

      mutation.mutate(
        {
          baseRevision,
          headRevision,
          headRepositoryId,
          customPrompt: overridePrompt,
          previousJobId: previousJobIdRef.current,
        },
        {
          onSuccess: data => {
            previousJobIdRef.current = data.jobId
            if (data.isStale) {
              setIsStale(true)
              removeInsertedText()
            } else if (data.completion) {
              replaceInsertedText(data.completion)
            } else {
              removeInsertedText()
            }
          },
          onError: (error: Error) => {
            const message = error.message || 'Copilot encountered a temporary error.'
            replaceInsertedText(`[${message}]`)
          },
        },
      )
    },
    [
      mutation,
      baseRevision,
      headRevision,
      headRepositoryId,
      getEditorElement,
      emitChange,
      replaceInsertedText,
      removeInsertedText,
    ],
  )

  const discardGeneration = useCallback(() => {
    mutation.reset()
    removeInsertedText()
  }, [mutation, removeInsertedText])

  const openPromptEditor = useCallback(() => {
    setMenuOpen(false)
    setPromptEditorOpen(true)
  }, [])

  const closePromptEditor = useCallback(() => {
    setPromptEditorOpen(false)
  }, [])

  const resetCustomPrompt = useCallback(() => {
    setCustomPrompt(DEFAULT_SUMMARY_PROMPT)
  }, [])

  const submitCustomPrompt = useCallback(() => {
    generateSummary(customPrompt)
  }, [generateSummary, customPrompt])

  return {
    isGenerating,
    errorMessage,
    isStale,
    menuOpen,
    setMenuOpen,
    promptEditorOpen,
    openPromptEditor,
    closePromptEditor,
    customPrompt,
    setCustomPrompt,
    resetCustomPrompt,
    submitCustomPrompt,
    generateSummary: () => generateSummary(),
    discardGeneration,
  }
}

export type UsePullRequestSummaryResult = ReturnType<typeof usePullRequestSummary>
